<template>
  <app-layout>
    <app-header>
      <div class="flex items-end">
        <div class="flex-1">
          <icon-link
            :href="route('home')"
            icon="arrow-thin-left-circle"
            class="icons-secondary"
          >
            Back to dashboard
          </icon-link>

          <h1>{{ competition.title }} Competition</h1>
          <p v-if="competition.funding_pots[0]" class="text-grey-200 text-xs">
            {{ competition.funding_pots[0].name }}
          </p>
        </div>
        <div class="hidden md:block">
          <icon-link
            v-if="competition.guidance_pack_file_id"
            :href="
              route('competitions.download', { competition: competition.uuid })
            "
            icon="cheveron-down-circle"
            icon-placement="right"
          >
            Download guidance pack
          </icon-link>
        </div>
      </div>
    </app-header>
    <div
      v-if="competition.statuses.closed"
      class="bg-neutral-100 -mt-16 mb-8 py-4"
    >
      <div class="container flex items-center">
        <icon icon="important" class="w-8 h-8 icons-neutral mr-4" />
        <p class="text-neutral-900">
          This competition has now closed.
        </p>
      </div>
    </div>
    <article class="container">
      <div class="border-b-2 mb-12 flex flex-wrap">
        <div
          v-if="competition.min_grant_per_project"
          class="w-full lg:w-1/3 pb-12"
        >
          <h4 class="text-2xs tracking-wide uppercase text-grey-600">
            Minimum Project Grant
          </h4>
          <p class="text-2xl sm:text-5xl text-primary-600 leading-none">
            £{{ competition.min_grant_per_project.toLocaleString("en-EN") }}
          </p>
        </div>

        <div
          v-if="competition.max_grant_per_project"
          class="w-full lg:w-1/3 pb-12"
        >
          <h4 class="text-2xs tracking-wide uppercase text-grey-600">
            Maximum Project Grant
          </h4>
          <p class="text-2xl sm:text-5xl text-primary-600 leading-none">
            £{{ competition.max_grant_per_project.toLocaleString("en-EN") }}
          </p>
        </div>

        <div v-if="competition.min_project_costs" class="w-full lg:w-1/3 pb-12">
          <h4 class="text-2xs tracking-wide uppercase text-grey-600">
            Minimum Project Costs
          </h4>
          <p class="text-2xl sm:text-5xl text-primary-600 leading-none">
            £{{ competition.min_project_costs.toLocaleString("en-EN") }}
          </p>
        </div>

        <div v-if="competition.max_project_costs" class="w-full lg:w-1/3 pb-12">
          <h4 class="text-2xs tracking-wide uppercase text-grey-600">
            Maximum Project Costs
          </h4>
          <p class="text-2xl sm:text-5xl text-primary-600 leading-none">
            £{{ competition.max_project_costs.toLocaleString("en-EN") }}
          </p>
        </div>

        <div
          v-if="competition.max_project_grant_intervention_rate"
          class="w-full lg:w-1/3 pb-12"
        >
          <h4 class="text-2xs tracking-wide uppercase text-grey-600">
            Maximum Grant Intervention
          </h4>
          <p class="text-2xl sm:text-5xl text-primary-600 leading-none">
            {{
              competition.max_project_grant_intervention_rate.toLocaleString(
                "en-EN"
              )
            }}%
          </p>
        </div>
      </div>

      <div
        v-if="
          competition.statuses.open &&
            !competition.company_can_apply &&
            !competition.user_has_project
        "
        class="border mb-12 border-primary-400 bg-primary-100 p-4 lg:p-6 text-primary-900 shadow-lg"
      >
        <h4 class="flex items-center mb-4">
          <icon icon="important" class="w-8 h-8 mr-2 icons-secondary" />
          Multiple competition leads are not permitted.
        </h4>
        <p>
          You have already applied for another competition in the
          {{ competition.funding_pots[0].name }} funding cycle.
        </p>
      </div>

      <content-section class="pb-12 border-b-2 border-secondary-500">
        <template #title>
          <h2>Introduction</h2>
          <p class="italic text-sm text-grey-700">
            An overview of this competition and what constitutes a
            {{ competition.type.name }} project.
          </p>
        </template>
        <div v-html="parseMarkdown(competition.overview, false)" />
      </content-section>

      <content-section class="pb-12 border-b-2 border-secondary-500">
        <template #title>
          <h2>Funding eligibility</h2>
          <p class="italic text-sm text-grey-700">
            Please make sure that your project meets this competitions
            eligibility criteria.
          </p>
        </template>

        <div
          v-html="parseMarkdown($sanitizeHtml(competition.funding_eligibility))"
        />
      </content-section>

      <content-section class="pb-12 border-b-2 border-secondary-500">
        <template #title>
          <h2>Funding metrics</h2>
          <p class="italic text-sm text-grey-700">
            Your application for this competition will be measured against this
            criteria.
          </p>
        </template>

        <div>
          <div
            v-if="competition.funding_metrics"
            v-html="parseMarkdown($sanitizeHtml(competition.funding_metrics))"
          />
          <ul v-else>
            <li v-if="competition.min_grant_per_project">
              Minimum total project grant:
              <strong>
                £{{ competition.min_grant_per_project.toLocaleString("en-EN") }}
              </strong>
            </li>
            <li v-if="competition.max_grant_per_project">
              Maximum total project grant:
              <strong>
                £{{ competition.max_grant_per_project.toLocaleString("en-EN") }}
              </strong>
            </li>
            <li v-if="competition.min_project_costs">
              Minimum total project costs:
              <strong>
                £{{ competition.min_project_costs.toLocaleString("en-EN") }}
              </strong>
            </li>
            <li v-if="competition.max_project_costs">
              <icon
                icon="cheveron-right-circle"
                class="w-6 h-6 mr-2 icons-secondary align-middle mb-0 leading-none"
              />
              Maximum total project costs:
              <strong>
                £{{ competition.max_project_costs.toLocaleString("en-EN") }}
              </strong>
            </li>
            <li v-if="competition.max_project_grant_intervention_rate">
              Maximum project grant intervention rate:
              <strong>
                {{ competition.max_project_grant_intervention_rate }}%
              </strong>
            </li>
            <li v-if="competition.max_partner_grant_intervention_rate">
              Maximum partner grant intervention rate:
              <strong>
                {{ competition.max_partner_grant_intervention_rate }}%
              </strong>
            </li>
            <li v-if="competition.max_share_per_partner">
              Maximum share per partner:
              <strong>{{ competition.max_share_per_partner }}%</strong> of total
              project grant
            </li>
            <li v-if="competition.large_company_max_grant_threshold">
              Large companies cannot receive
              <strong>
                &gt;{{ competition.large_company_max_grant_threshold }}%
              </strong>
              of total project grant
            </li>
            <li
              v-if="
                competition.min_project_partners &&
                  competition.max_project_partners
              "
            >
              Number of partners (including Lead) per project:
              <strong>
                {{ competition.min_project_partners + 1 }} to
                {{ competition.max_project_partners + 1 }}
              </strong>
            </li>
          </ul>
        </div>
      </content-section>

      <content-section>
        <template #title>
          <h2>Project timeline</h2>
          <p class="italic text-sm text-grey-700">
            This is the full schedule of the competition, from the application
            phase to final review.
          </p>
        </template>
        <ul
          class="list-reset border-l-4 border-secondary-200 border-dashed mb-12"
        >
          <li
            v-for="(milestone, index) in timeline"
            :key="index"
            class="pl-6 pb-6 relative"
          >
            <icon
              icon="cheveron-right-circle"
              class="w-5 h-5 absolute pin-l -ml-3 mt-px icons-secondary"
            />
            <time
              :datetime="milestone.date"
              class="italic text-sm text-grey-700"
              >{{ formatDate(parseDate(milestone.date), "do MMMM yyyy") }}</time
            >
            <h4 class="mt-0">
              {{ milestone.title }}
            </h4>
            <div
              v-if="milestone.description"
              v-html="parseMarkdown($sanitizeHtml(milestone.description))"
            />
          </li>
        </ul>
        <div
          v-if="competition.guidance_pack_file_id"
          class="border mb-6 border-primary-400 bg-white p-4 lg:p-6 text-primary-900 shadow-lg"
        >
          <h4 class="flex items-center mb-4">
            <icon icon="map" class="w-8 h-8 mr-2 icons-secondary" />
            Guidance Pack
          </h4>
          <p>
            Please make sure you download and review the application guidance
            pack and use this information to give you the greatest chance of
            success.
          </p>
          <icon-link
            icon="cheveron-down-circle"
            icon-placement="right"
            :href="
              route('competitions.download', { competition: competition.uuid })
            "
            target="_blank"
            download
          >
            Download guidance documents
          </icon-link>
        </div>
      </content-section>
      <content-section>
        <template #prefix>
          <div class="mb-8 lg:w-1/3 lg:pt-6">
            <icon-link :href="route('home')" icon="arrow-thin-left-circle">
              Back to dashboard
            </icon-link>
          </div>
        </template>
        <div class="flex flex-wrap">
          <app-button
            v-if="competition.user_has_projects.length > 1"
            class="max-w-xs mr-4 mb-3"
            @click="viewMyApplications"
          >
            View my applications
          </app-button>
          <app-button
            v-else-if="competition.user_has_projects.length == 1"
            class="max-w-xs mr-4 mb-3"
            @click="viewProject"
          >
            {{
              competition.user_has_project.submitted_at ||
              competition.statuses.closed
                ? "View Application"
                : "Continue Application"
            }}
          </app-button>

          <app-button
            class="max-w-xs mr-4 mb-3"
            :disabled="!canApply"
            @click="confirmationModalVisible = true"
          >
            {{ competitionButtonText }}
          </app-button>
        </div>
      </content-section>
      <portal to="modals">
        <app-modal
          v-if="confirmationModalVisible && canApply"
          class="text-white"
          @close="confirmationModalVisible = false"
        >
          <div class="max-w-md mx-auto content">
            <icon icon="important" class="icons-primary w-16 h-16 mb-2" />
            <h2 class="mb-1">
              Application consent
            </h2>
            <p>
              By applying for this funding I have read and understood APC’s
              <a
                :href="route('terms')"
                target="_blank"
                class="text-primary-300 hover:text-primary-100"
                >terms and conditions</a
              ><template v-if="competition.guidance_pack_file_id">
                , as well the guidance notes included in the
                <a
                  target="_blank"
                  class="text-primary-300 hover:text-primary-100"
                  :href="
                    route('competitions.download', {
                      competition: competition.uuid
                    })
                  "
                  download
                >
                  guidance pack</a
                > </template
              >.
            </p>
            <p>
              You are required to submit your completed application by
              <strong>{{
                formatDate(
                  parseDate(competition.application_end_date),
                  "do MMMM yyyy"
                )
              }}</strong
              >.
            </p>
            <p>You can save this application to complete at a later date.</p>
            <p class="mt-12">
              <app-button colour="confirm" @click="apply">
                I agree
              </app-button>
              <app-button
                colour="close"
                @click="confirmationModalVisible = false"
              >
                I disagree
              </app-button>
            </p>
          </div>
        </app-modal>
      </portal>
    </article>
  </app-layout>
</template>

<script>
import format from "date-fns/format";
import parseISO from "date-fns/parseISO";
import isBefore from "date-fns/isBefore";
export default {
  name: "CompetitionView",

  data() {
    return {
      confirmationModalVisible: false,
      timelineItems: []
    };
  },
  computed: {
    canApply() {
      return (
        this.competition.statuses.open && this.competition.company_can_apply
      );
    },

    competition() {
      return this.state.competition;
    },

    competitionButtonText() {
      return this.competition.statuses.closed
        ? `Closed ${this.formatDate(
            this.parseDate(this.competition.application_end_date),
            "do MMMM yyyy"
          )}`
        : !this.competition.statuses.open
        ? `Opens ${this.formatDate(
            this.parseDate(this.competition.application_start_date),
            "do MMMM yyyy"
          )}`
        : this.competition.user_has_project &&
          this.competition.user_has_projects.length > 1
        ? "Start a new application"
        : "Start your application";
    },

    timeline() {
      return this.timelineItems.slice(0).sort((a, b) => {
        if (isBefore(this.parseDate(a.date), this.parseDate(b.date))) return -1;
        return 1;
      });
    }
  },
  created() {
    const {
      published_at,
      start_date,
      application_start_date,
      application_end_date,
      contracting_start_date,
      contracting_end_date,
      end_date,
      milestones
    } = this.competition;

    this.timelineItems.push(...(milestones ? milestones : []));

    if (published_at) {
      this.timelineItems.push({
        date: published_at,
        title: "Competition published",
        description: "The competition is now available for you review."
      });
    }

    if (start_date) {
      this.timelineItems.push({
        date: start_date,
        title: "Project starts",
        description: "The awarded projects can begin."
      });
    }

    if (application_start_date) {
      this.timelineItems.push({
        date: application_start_date,
        title: "Applications open",
        description: "You can now start your competition application."
      });
    }

    if (application_end_date) {
      this.timelineItems.push({
        date: application_end_date,
        title: "Applications closed",
        description:
          "You must have submitted your competition's application by noon on this date."
      });
    }

    if (contracting_start_date) {
      this.timelineItems.push({
        date: contracting_start_date,
        title: "Winners notified & contracting begins",
        description:
          "Successful applicants will be notified and sent their contracts for review and signing."
      });
    }

    if (contracting_end_date) {
      this.timelineItems.push({
        date: contracting_end_date,
        title: "Project contracting end",
        description: "All contracts must be reviewed and signed by this date."
      });
    }

    if (end_date) {
      this.timelineItems.push({
        date: end_date,
        title: "Project finishes",
        description:
          "This is the latest allowable completion date for this competition."
      });
    }
  },
  methods: {
    formatDate: format,

    parseDate: parseISO,

    apply() {
      const { uuid, company_can_apply } = this.state.competition;
      if (!company_can_apply) return;
      axios
        .post(route("competitions.apply", { competition: uuid }))
        .then(({ data }) => this.success(data))
        .catch(() =>
          alert(
            "Sorry, there appears to have been an error creating your application. Please try again later"
          )
        );
    },

    viewProject() {
      if (!this.competition.user_has_project) return;
      return Turbolinks.visit(
        route("applications.view", this.competition.user_has_project.uuid)
      );
    },

    viewMyApplications() {
      if (!this.competition.user_has_projects) return;
      return Turbolinks.visit(route("applications.index"));
    },

    success({ project_id }) {
      Turbolinks.visit(route("applications.view", { id: project_id }));
    }
  }
};
</script>
